import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { useRef } from "react";
import { DropTargetMonitor, useDrag, useDrop, XYCoord } from "react-dnd";
import { Rule } from "../models/rule";
import Trash from "./icons/trash";

export type CurrentRuleProps = {
  rule: Rule;
  index: number;
  moveRule: (dragIndex: number, hoverIndex: number) => void;
  deleteRule: (rule: Rule) => void;
};

export interface DragItem {
  index: number;
  id: string;
  type: string;
}

const CurrentRule = (props: CurrentRuleProps) => {
  const rule = props.rule;
  const index = props.index;
  const moveRule = props.moveRule;
  const deleteRule = props.deleteRule;

  const ref = useRef<HTMLDivElement>(null);
  const [, drop] = useDrop({
    accept: "current-rule",
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      };
    },
    hover: (item: DragItem, monitor: DropTargetMonitor) => {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      // Determine rectangle on screen
      const hoverBoundingRect = ref.current?.getBoundingClientRect();

      // Get vertical middle
      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

      // Determine mouse position
      const clientOffset = monitor.getClientOffset();

      // Get pixels to the top
      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return;
      }

      // Time to actually perform the action
      moveRule(dragIndex, hoverIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  const [{ isDragging }, drag] = useDrag({
    type: "current-rule",
    item: () => {
      return { rule, index };
    },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const opacity = isDragging ? 0 : 1;
  drag(drop(ref));

  return (
    <div
      ref={ref}
      className={`rounded-md border p-2 bg-white cursor-move shadow ${
        opacity === 0 ? "opacity-40" : ""
      }`}
    >
      <div className="rounded-md flex items-center p-4 gap-2 bg-gray-100 text-gray-400 text-sm">
        <div className="w-4/5 md:flex-grow flex items-center justify-start">
          <EllipsisVerticalIcon className={`h-4 w-4 -ml-2`} />
          <EllipsisVerticalIcon className={`h-4 w-4 -ml-5 mr-2`} />
          <div className="rounded-full bg-red-500 text-white flex justify-center items-center h-6 w-6 mr-2">
            <div>{props.index + 1}</div>
          </div>
          <div className="text-black w-3/4 md:w-auto">{rule.name}</div>
        </div>
        <button
          className="h-6 w-6"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            deleteRule(rule);
          }}
        >
          <Trash className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

export default CurrentRule;
